/* eslint "react/jsx-no-useless-fragment": "off" */

import { page as pageSchema } from '@stereoagency/sanity-schema';
import SimpleHero from '@/components/sections/SimpleHero.client';
import LargeHero from '@/components/sections/LargeHero.client';
import MediaSection from '@/components/sections/MediaSection.client';
import KeyFiguresSection from '@/components/sections/KeyFiguresSection.client';
import BigTitleSection from '@/components/sections/BigTitleSection.client';
import ProgramsListSection from '@/components/sections/ProgramsListSection.client';
import PictureAndContentSection from '@/components/sections/PictureAndContentSection.client';
import LogoCarouselSection from '@/components/sections/LogoCarouselSection.client';
import StepListSection from '@/components/sections/StepListSection.client';
import SimpleDatalistSection from '@/components/sections/SimpleDatalistSection.client';
import ImageGallerySection from '@/components/sections/ImageGallerySection.client';
import SimpleContentSection from '@/components/sections/SimpleContentSection.client';
import CallToActionCardsGridSection from '@/components/sections/CallToActionCardsGridSection.client';
import LogoCardsGridSection from '@/components/sections/LogoCardsGridSection.client';
import SimpleCardsListSection from '@/components/sections/SimpleCardsListSection.client';
import DividerSection from '@/components/sections/DividerSection.client';
import ScrollAnchorSection from '@/components/sections/ScrollAnchorSection.client';
import { ContactSection } from '@/components/sections/ContactSection';
import { ReportsGridSection } from '@/components/sections/ReportsGridSection';
import { ImageActionCardsSection } from '@/components/sections/ImageActionCardsSection.client';
import { DonationBoxSection } from '@/components/sections/DonationBoxSection.client';
import {
  TeamSection,
  DraftModeTeamSection
} from '@/components/sections/TeamSection';
import {
  DraftModeLatestNewsGridSection,
  LatestNewsGridSection
} from '@/components/sections/LatestNewsGridSection';
import {
  DraftModeResearchGroupListingSection,
  ResearchGroupListingSection
} from '@/components/sections/ResearchGroupListingSection';
import {
  DraftModeArticleListingSection,
  ArticleListingSection
} from '@/components/sections/ArticleListingSection';
import {
  DraftModeEventListingSection,
  EventListingSection
} from '@/components/sections/EventListingSection';
import {
  DraftModeJobListingSection,
  JobListingSection
} from '@/components/sections/JobListingSection';
import {
  DraftModeDiseaseListingSection,
  DiseaseListingSection
} from '@/components/sections/DiseaseListingSection';
import {
  DraftModeProfileListingSection,
  ProfileListingSection
} from '@/components/sections/ProfileListingSection';
import {
  DraftModeDepartmentsSection,
  DepartmentsSection
} from '@/components/sections/DepartmentsSection';
import {
  DraftModeResearchPlatformListingSection,
  ResearchPlatformListingSection
} from '@/components/sections/ResearchPlatformListingSection';
import LegacySimulatorSection from '@/components/sections/LegacySimulatorSection.client';
import TextVideo from '../../sections/TextVideo.client';

const [, pageType] = pageSchema.schema.builder
  .pick(['_id', 'sections', 'pattern'])
  .first()
  .use();

export interface Props {
  isPreview?: boolean;
  sections: NonNullable<(typeof pageType)['sections']>;
}

export default function PageSections({ isPreview, sections }: Props) {
  return (
    <>
      {sections?.map((section: any) => {
        if (section._type === 'dividerSection') {
          return <DividerSection key={section._key} />;
        }

        if (section._type === 'scrollAnchorSection') {
          return <ScrollAnchorSection key={section._key} {...section} />;
        }

        if (section._type === 'simpleHero') {
          return <SimpleHero key={section._key} {...section} />;
        }

        if (section._type === 'largeHero') {
          return <LargeHero key={section._key} {...section} />;
        }

        if (section._type === 'mediaSection') {
          return <MediaSection key={section._key} {...section} />;
        }

        if (section._type === 'keyFiguresSection') {
          return <KeyFiguresSection key={section._key} {...section} />;
        }

        if (section._type === 'bigTitleSection') {
          return <BigTitleSection key={section._key} {...section} />;
        }

        if (section._type === 'programsListSection') {
          return <ProgramsListSection key={section._key} {...section} />;
        }

        if (section._type === 'pictureAndContentSection') {
          return <PictureAndContentSection key={section._key} {...section} />;
        }

        if (section._type === 'logoCarouselSection') {
          return <LogoCarouselSection key={section._key} {...section} />;
        }

        if (section._type === 'stepListSection') {
          return <StepListSection key={section._key} {...section} />;
        }

        if (section._type === 'simpleDatalistSection') {
          return <SimpleDatalistSection key={section._key} {...section} />;
        }

        if (section._type === 'imageGallerySection') {
          return <ImageGallerySection key={section._key} {...section} />;
        }

        if (section._type === 'simpleContentSection') {
          return <SimpleContentSection key={section._key} {...section} />;
        }

        if (section._type === 'teamSection') {
          if (isPreview) {
            return <DraftModeTeamSection key={section._key} {...section} />;
          }

          return <TeamSection key={section._key} {...section} />;
        }

        if (section._type === 'callToActionCardsGridSection') {
          return (
            <CallToActionCardsGridSection key={section._key} {...section} />
          );
        }

        if (section._type === 'logoCardsGridSection') {
          return <LogoCardsGridSection key={section._key} {...section} />;
        }

        if (section._type === 'simpleCardsListSection') {
          return <SimpleCardsListSection key={section._key} {...section} />;
        }

        // data powered sections
        if (section._type === 'latestNewsGridSection') {
          if (isPreview) {
            return (
              <DraftModeLatestNewsGridSection key={section._key} {...section} />
            );
          }

          return <LatestNewsGridSection key={section._key} {...section} />;
        }

        if (section._type === 'researchGroupListingSection') {
          if (isPreview) {
            return (
              <DraftModeResearchGroupListingSection
                key={section._key}
                {...section}
              />
            );
          }

          return (
            <ResearchGroupListingSection key={section._key} {...section} />
          );
        }

        if (section._type === 'articleListingSection') {
          if (isPreview) {
            return (
              <DraftModeArticleListingSection key={section._key} {...section} />
            );
          }

          return <ArticleListingSection key={section._key} {...section} />;
        }

        if (section._type === 'eventListingSection') {
          if (isPreview) {
            return (
              <DraftModeEventListingSection key={section._key} {...section} />
            );
          }

          return <EventListingSection key={section._key} {...section} />;
        }

        if (section._type === 'jobListingSection') {
          if (isPreview) {
            return (
              <DraftModeJobListingSection key={section._key} {...section} />
            );
          }

          return <JobListingSection key={section._key} {...section} />;
        }

        if (section._type === 'diseaseListingSection') {
          if (isPreview) {
            return (
              <DraftModeDiseaseListingSection key={section._key} {...section} />
            );
          }

          return <DiseaseListingSection key={section._key} {...section} />;
        }

        if (section._type === 'profileListingSection') {
          if (isPreview) {
            return (
              <DraftModeProfileListingSection key={section._key} {...section} />
            );
          }

          return <ProfileListingSection key={section._key} {...section} />;
        }

        if (section._type === 'departmentsSection') {
          if (isPreview) {
            return (
              <DraftModeDepartmentsSection key={section._key} {...section} />
            );
          }

          return <DepartmentsSection key={section._key} {...section} />;
        }

        if (section._type === 'researchPlatformListingSection') {
          if (isPreview) {
            return (
              <DraftModeResearchPlatformListingSection
                key={section._key}
                {...section}
              />
            );
          }

          return (
            <ResearchPlatformListingSection key={section._key} {...section} />
          );
        }

        if (section._type === 'contactSection') {
          return <ContactSection key={section._key} {...section} />;
        }

        if (section._type === 'reportsGridSection') {
          return <ReportsGridSection key={section._key} {...section} />;
        }

        if (section._type === 'imageActionCardsSection') {
          return <ImageActionCardsSection key={section._key} {...section} />;
        }

        if (section._type === 'donationBoxSection') {
          return <DonationBoxSection key={section._key} {...section} />;
        }

        if (section._type === 'legacySimulatorSection') {
          return <LegacySimulatorSection key={section._key} {...section} />;
        }

        // @ts-ignore
        if (section._type === 'textVideo') {
          // @ts-ignore
          return <TextVideo key={section._key} {...section} />;
        }

        return null;
      })}
    </>
  );
}
